import GuiContext from "config/context/guiContext";
import ToastContext from "config/context/toastContext";
import useCustomStyles from "config/useCustomStyles";
import useTheme from "config/useTheme";
import styles from "css/privateKey.module.css";
import styles2 from "css/addNewDatabase.module.css";
import PropTypes from "prop-types";
import { useContext, useState, useEffect } from "react";
import Modal from "react-modal";
import Button from "../shared/button/Button";
import InputField from "components/shared/input-field/InputField";
import { useGenerateKeypair } from "api/authentication/usePrivateKey";
import { useRemoveDatabase } from "features/compositions/services/useRemoveDatabase";
import { useInvalidateMemory } from "features/memory";
import { useAccessCheck } from "features/compositions/services/useAccessCheck";
import downloadFile from "lib/DownloadFile";
import { ExportIcon } from "assets/icons";
import ActionLoader from "components/shared/status/ActionLoader";
import StepProgress from "components/shared/status/StepProgress";
import Irreversible from "components/shared/status/Irreversible";
import PrivateKeyField from "components/shared/private-key-field/PrivateKeyField";

const AddNewAccount = ({ typeCall, openModal, setOpenModal }) => {
  const [creatorName, setCreatorName] = useState("");
  const [newPublicKey, setNewPublicKey] = useState("");
  const [newPrivateKey, setNewPrivateKey] = useState("");
  const [firstInput, addFirstInput] = useState("");
  const [lastInput, addLastInput] = useState("");
  const [serverMessage, setServerMessage] = useState("");
  // Serverova poruka o ishodu brisanja, PREPISANA iz hook-a pre nego što ga
  // `resetRemove()` isprazni. Bez ovoga ekran uspeha uvek dobije `null`: reset
  // se poziva u istom potezu u kom se prelazi na taj ekran.
  const [removedMessage, setRemovedMessage] = useState("");
  // Skidanje kontrakta je nivo dublje od uklanjanja baze, pa stoji kao izbor
  // unutar istog toka — ne kao zaseban dijalog, jer bi dva skoro ista prozora
  // zvala na pogrešan izbor.
  // Koja je radnja pokrenuta — da se posle znaju i poruka i to da li
  // lokalni spisak treba dirati. Ranije se to izvodilo iz checkbox-a.
  const [lastAction, setLastAction] = useState(null);

  const [success, setSuccess] = useState(false);
  const {
    userData,
    selected: { selectedServer },
    linkDatabase,
    unlinkDatabase,
    URL,
  } = useContext(GuiContext);
  const { dark } = useTheme();
  // Stvarno uklanjanje baze: pražnjenje po kompoziciji, pa uklanjanje, pa
  // skidanje sa liste. Vidi useRemoveDatabase — zatečeni put je radio samo
  // poslednji korak i pri tom tvrdio da je baza obrisana.
  const {
    removeDatabase,
    running: removing,
    error: removeError,
    done: removeDone,
    progress: removeProgress,
    reset: resetRemove,
    destroyedInBlock,
    outcome: removeOutcome,
  } = useRemoveDatabase();
  const invalidateMemory = useInvalidateMemory();
  const {
    check: runAccessCheck,
    checking: accessChecking,
    result: accessResult,
    reset: resetAccessCheck,
  } = useAccessCheck();
  // Brojač referenci. Nula znači „nije provereno", ne „nema nikoga" — dok
  // provera nije stigla, dugmad se ne blokiraju.
  const sharedWith = accessResult?.keys_on_auth || 0;
  const { insertIntoToastList } = useContext(ToastContext);
  const [mode, setMode] = useState("start");
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const customStyles = useCustomStyles();

  const {
    response: keypairResponse,
    loading: keypairLoading,
    error: keypairError,
    getKeypair,
  } = useGenerateKeypair();

  useEffect(() => {
    if (keypairResponse?.privateKey) {
      setNewPrivateKey(keypairResponse.privateKey);
      setChecked(false); // Reset checkbox when new keys are generated
    }
    if (keypairResponse?.publicKey) {
      setNewPublicKey(keypairResponse.publicKey);
      setChecked(false); // Reset checkbox when new keys are generated
    }
  }, [keypairResponse]);

  // Baza se sa liste skida TEK kad je stvarno uklonjena sa lanca. Zatečeni tok
  // je radio obrnuto — prvo je nestajala iz GUI-ja, a na lancu ostajala.
  //
  // I SAMO ako je skidanje traženo. Ovaj poziv je bio bezuslovan, napisan dok
  // je brisanje uvek skidalo i sa spiska; posle razdvajanja je to značilo da
  // lanac zadrži vezu a pregledač je izgubi — spisak i stvarnost se raziđu do
  // sledeće prijave.
  useEffect(() => {
    if (!removeDone) return;
    if (lastAction === "unlink" || lastAction === "purge") unlinkDatabase(firstInput);
    // Zauzeće na lancu se posle brisanja oslobodi ISTOG TRENUTKA (izmereno
    // 06.10.2026: 631 700 -> 3 140), pa widget mora da ga pročita ponovo.
    //
    // Zašto je ovo trebalo samo za „delete" i „empty": `unlink` i `purge`
    // menjaju izabrani nalog kroz `unlinkDatabase`, pa se keš memorije poništi
    // SAM OD SEBE jer mu se promeni ključ. Ta dva su slučajno radila, ova dva
    // nikad — i to je bilo Goranovo zapažanje.
    invalidateMemory();
    insertIntoToastList(
      lastAction === "unlink"
        ? "Database removed from your list"
        : lastAction === "purge"
          ? "Database deleted and removed from your list"
          : lastAction === "empty"
            ? "Database emptied"
            : "Database deleted",
      "success",
      "unlinkDatabase",
    );
    setMode("account_removed");
    // Prepisati PRE reset-a — on briše i `outcome`.
    setRemovedMessage(removeOutcome || "");
    resetRemove();
    // Namerno visi SAMO o `removeDone`. Telo skida bazu sa spiska i javlja
    // poruku, dakle sme da se izvrši tačno jednom po završenom brisanju.
    // `firstInput` i `lastAction` se menjaju dok korisnik kuca; da su u
    // zavisnostima, između prelaska `removeDone` u tačno i `resetRemove()`
    // dobili bismo dvostruko skidanje sa spiska i dvostruku poruku.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeDone]);

  const handleExportKeys = () => {
    if (newPublicKey && newPrivateKey) {
      const content = `Public Key:\n${newPublicKey}\n\nPrivate Key:\n${newPrivateKey}`;
      const filename = `database_keys_${Date.now()}.txt`;
      downloadFile(content, filename, { type: 'text/plain;charset=utf-8;' });
    }
  };

  const handleCancel = () => {
    addFirstInput("");
    addLastInput("");
    setCreatorName("");
    setNewPublicKey("");
    setNewPrivateKey("");
    setServerMessage("");
    setMode("start");
    setOpenModal(false);
    setChecked(false);
    // Razoran izbor se ne pamti između otvaranja — inače bi jednom štiklirano
    // sačekalo sledeću bazu.
    setLastAction(null);
    resetAccessCheck();
    setSuccess(false);
  };

  const handleSubmit = () => {
    setServerMessage("");
    let url = "";

    const data = {};

    if (mode === "createNew") {
      url = `${URL}/api/account/create_account`;
      data.guiname = userData.guiname;
      data.public_key = newPublicKey;
      data.creator_name = creatorName;
      data.server_url = selectedServer;
      data.account_name = firstInput;
      data.creator_key = lastInput;
      data.import = true;
    } else {
      url = `${URL}/api/inerygui/${typeCall}`;
      data.guiname = userData.guiname;
      data.server_url = selectedServer;
      data.account_name = firstInput;
      data.private_key = lastInput;
    }

    async function sendDataToServer() {
      setIsSubmitting(true);
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
        const res = await response.json();
        if (response.ok && res.status === true) {
          setServerMessage("");
          setSuccess(true);
          switch (typeCall) {
            case "link_database":
              if (mode === "import") {
                linkDatabase(firstInput);
                insertIntoToastList(
                  "Database is imported",
                  "success",
                  "importAccount",
                );
                setMode("account_imported");
              } else {
                insertIntoToastList(
                  "Database is created",
                  "success",
                  "linkDatabase",
                );
                linkDatabase(firstInput);
                setMode("account_created");
              }
              break;
            case "unlink_database":
              unlinkDatabase(firstInput);
              insertIntoToastList(
                "Database is removed",
                "success",
                "unlinkDatabase",
              );
              setMode("account_removed");
              break;
            default:
              break;
          }
        } else {
          let errorMessageFromServer = "";
          switch (typeCall) {
            case "link_database":
              if (mode === "createNew") {
                errorMessageFromServer = "Transaction is not authorized";
                setServerMessage(errorMessageFromServer);
              } else {
                errorMessageFromServer = "Unable to import database";
                insertIntoToastList(errorMessageFromServer, "error", "linkDatabase");
              }
              break;
            case "unlink_database":
              errorMessageFromServer = "Unable to remove database";
              insertIntoToastList(errorMessageFromServer, "error", "linkDatabase");
              break;
            case "createNew":
              errorMessageFromServer = "Transaction is not authorized";
              setServerMessage(errorMessageFromServer);
              break;
            default:
              break;
          }
          setSuccess(false);
        }
      } catch (error) {
        console.error(error, "greska");
        if (mode === "createNew") {
          setServerMessage("Transaction is not authorized");
          setSuccess(false);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
    sendDataToServer();
  };

  const renderModalContent = (typeCall, mode) => {
    switch (typeCall) {
      case "link_database":
        switch (mode) {
          case "start":
            return (
              <>
                <h2>Add New Database</h2>
                <div className="container column gap-1 align-center">
                  <div className="container column gap-05">
                    <Button onClick={() => {
                      setServerMessage("");
                      setMode("import");
                    }} expand="full">
                      Import Database
                    </Button>
                    <Button
                      onClick={() => {
                        setServerMessage("");
                        setChecked(false);
                        setMode("createNew");
                      }}
                      expand="full"
                      variant="secondary"
                    >
                      Add Database
                    </Button>
                  </div>
                  <Button
                    onClick={handleCancel}
                    buttonType="text"
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            );
          case "import":
            return (
              <>
                <h2>Import database</h2>
                <InputField
                  value={firstInput}
                  onChange={(e) => addFirstInput(e.target.value)}
                  type="text"
                  placeholder="Database Name"
                />
                <PrivateKeyField
                  value={lastInput}
                  onChange={(e) => addLastInput(e.target.value)}
                  placeholder="Private Key"
                />
                <div className="column align-center gap-05">
                  <Button onClick={handleSubmit}>Confirm</Button>
                  <Button buttonType="text" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
                {isSubmitting && <ActionLoader />}
              </>
            );
          case "createNew":
            return (
              <>
                <h2>Add new database</h2>
                <InputField
                  value={creatorName}
                  onChange={(e) => setCreatorName(e.target.value)}
                  type="text"
                  placeholder="Creator Name"
                />
                <InputField
                  value={firstInput}
                  onChange={(e) => addFirstInput(e.target.value)}
                  type="text"
                  placeholder="Database Name"
                />
                <div className={styles2.generate_keypair}>
                  <button
                    type="button"
                    className={styles2.generate_keypair_btn}
                    onClick={() => getKeypair()}
                    disabled={keypairLoading}
                  >
                    {keypairLoading ? "Generating..." : "Generate keypair"}
                  </button>

                  {newPublicKey && (
                    <p className={styles2.generated_key}>Public Key: {newPublicKey}</p>
                  )}
                  {newPrivateKey && (
                    <div className={styles2.generated_key_wrapper}>
                      <p className={styles2.generated_key}>
                        Private Key: {newPrivateKey.substring(0, 3)}...
                      </p>
                      {(newPublicKey && newPrivateKey) && (
                        <button 
                          type="button" 
                          className={styles2.export_button}
                          onClick={handleExportKeys}
                          title="Export keys to text file"
                        >
                          <ExportIcon />
                        </button>
                      )}
                    </div>
                  )}
                  {keypairError && (
                    <p className={styles.error}>{keypairError}</p>
                  )}
                </div>
                <PrivateKeyField
                  value={lastInput}
                  onChange={(e) => addLastInput(e.target.value)}
                  placeholder="Creator Private Key"
                />
                {(newPublicKey && newPrivateKey) && (
                  <p className={styles2.checkbox_text} onClick={() => setChecked((prev) => !prev)}>
                    <input 
                      checked={checked} 
                      type="checkbox" 
                      onChange={(e) => setChecked(e.target.checked)}
                    /> I have saved the keys
                  </p>
                )}
                <div className="column align-center gap-05">
                  <Button 
                    onClick={handleSubmit}
                    disabled={
                      !creatorName.trim() || 
                      !firstInput.trim() || 
                      !lastInput.trim() || 
                      ((newPublicKey && newPrivateKey) && !checked)
                    }
                  >
                    Confirm
                  </Button>
                  <Button
                    buttonType="text"
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
                {isSubmitting && <ActionLoader />}
              </>
            );
          // Uvoz je postavljao `account_imported`, a `switch` taj režim NIJE
          // imao — padalo je na `default: return null`, pa se modal iscrtavao
          // prazan: beo prozor na sivom zastoru, bez ijednog dugmeta, ni
          // `Cancel`. Uvoz bi na lancu prošao, a korisnik bi ostao zaglavljen i
          // razumno zaključio da se GUI zaledio. Slepa ulica, ne kvar u radu.
          case "account_imported":
            return (
              <>
                <div className={styles.text}>
                  <h2>Database "{firstInput}" is on your list</h2>
                </div>
                <p className={styles2?.warning || ""}>
                  Nothing was created on the chain — the account already existed
                  and now points at it from here. If it carries no contract yet,
                  an import will build one.
                </p>
                <div className={styles.btnGroup}>
                  <Button onClick={handleCancel}>OK</Button>
                </div>
              </>
            );
          case "account_created":
            return (
              <>
                <div className={styles.text}>
                  <h2>Database "{firstInput}" is successfully created</h2>
                </div>
                <div className={styles.btnGroup}>
                  <Button onClick={handleCancel}>OK</Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {" "}
                    {serverMessage}
                  </p>
                )}
              </>
            );
          default:
            return null;
        }
      // Skidanje sa spiska je ZASEBNA radnja, ne dodatak brisanju: ne dira
      // lanac — nalog, kontrakt i podaci ostaju, menja se samo ko je vidi.
      // Ključ se ipak traži, jer ruta njime dokazuje da pozivalac bazu
      // kontroliše; time se ne može slučajno napustiti nešto čiji ključ nemaš.
      case "detach_database":
        switch (mode) {
          case "start":
            return (
              <>
                <div className={styles.text}>
                  <h2>Remove Database From My List</h2>
                </div>
                <input
                  value={firstInput}
                  onChange={(e) => addFirstInput(e.target.value)}
                  type="text"
                  placeholder="Enter database name"
                />
                <PrivateKeyField
                  value={lastInput}
                  onChange={(e) => addLastInput(e.target.value)}
                  placeholder="Enter your private key"
                />
                <p className={styles2?.warning || ""}>
                  Nothing on the chain changes. The database, its contract and
                  every row stay exactly where they are; only your list loses
                  the entry. Add it back at any time with this same key.
                </p>
                <p className={styles2?.warning || ""}>
                  Whoever holds the private key still controls it — the list is
                  a bookmark, not ownership.
                </p>
                {removeError && <p className={styles.error}>{removeError}</p>}
                <div className={styles.btnGroup}>
                  <Button
                    disabled={accessChecking || !firstInput}
                    onClick={async () => {
                      await runAccessCheck(firstInput, lastInput);
                      setMode("detach_confirm");
                    }}
                  >
                    {accessChecking ? "Checking…" : "Continue"}
                  </Button>
                  <Button buttonType="text" variant="secondary" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </>
            );
          // Nalaz pre radnje. Skidanje sa spiska ne uništava ništa, ali baza
          // koju posle toga niko nema u spisku se kroz GUI više ne vidi — a
          // nazad je dovodi jedino `import`, uz ime i privatni ključ. Zato se
          // pre potvrde kaže tačno dvoje: da li je pozivalac može vratiti i da
          // li je još neko vidi.
          case "detach_confirm": {
            const holders = accessResult?.holders || [];
            const keyValid = !!accessResult?.key_valid;
            const unknown =
              !!accessResult?.unknown || accessResult?.scan_complete === false;
            const coOwned = (accessResult?.keys_on_auth || 0) > 1;
            // Skidanje poslednje baze bi ostavilo prazan spisak — stanje koje
            // GUI nema kako da prikaže. Ruta to odbija; ovde se kaže unapred,
            // da se ne sazna tek posle klika.
            const onlyOne = !!accessResult?.would_empty_list;
            return (
              <>
                <div className={styles.text}>
                  <h2>Remove {firstInput} from your list?</h2>
                </div>

                {unknown && (
                  <p className={styles.error}>
                    The check could not be completed, so it is not known whether
                    anyone else can still reach this database. Nothing has
                    changed yet.
                  </p>
                )}

                {onlyOne && (
                  <p className={styles.error}>
                    {firstInput} is your only database. Taking it off would
                    leave you with none, and no screen here would point at it
                    any more. Add another database first, or use Empty to clear
                    its contents and keep it.
                  </p>
                )}

                {!keyValid ? (
                  // Bez ključa nema nijedne radnje: i skidanje sa spiska i
                  // brisanje se potpisuju njime. Baza ostaje u spisku, što je
                  // ovde bezbedan ishod, a ne zastoj.
                  <p className={styles.error}>
                    {lastInput
                      ? `That private key does not match ${firstInput}.`
                      : `No private key entered for ${firstInput}.`}{" "}
                    Neither taking it off your list nor deleting it is possible
                    without it — both are signed with that key. Your list is
                    unchanged, and the database stays where you can still see it.
                  </p>
                ) : holders.length > 0 ? (
                  <p className={styles2?.warning || ""}>
                    {holders.length === 1
                      ? `${holders[0]} also has this database on their list.`
                      : `${holders.length} other users also have this database on their list: ${holders.join(", ")}.`}{" "}
                    It stays reachable for them, and your key still works if you
                    add it back later.
                  </p>
                ) : (
                  <>
                    <p className={styles2?.warning || ""}>
                      No other user of this GUI has {firstInput} on their list.
                      Once it is off yours, nothing here points to it — the rows
                      stay on the chain but no screen shows them.
                    </p>
                    <p className={styles2?.warning || ""}>
                      You hold the key, so you can bring it back through Add New
                      Database → import. That needs the name{" "}
                      <strong>{firstInput}</strong> and this same key. Keep both.
                    </p>
                    {coOwned && (
                      <p className={styles2?.warning || ""}>
                        Its permission carries {accessResult.keys_on_auth} keys,
                        so someone outside this GUI may hold one as well.
                      </p>
                    )}
                  </>
                )}

                {/* Brisanje sa ovog ekrana prolazi kroz sve kompozicije baš kao
                    i „Delete Database", pa mora i da javlja dokle je stiglo.
                    Bez ovoga je jedini znak života bio natpis na dugmetu. */}
                {removing && <StepProgress progress={removeProgress} />}
                {removeError && <p className={styles.error}>{removeError}</p>}

                <div className={styles.btnGroup}>
                  {keyValid && (
                    <Button
                      disabled={removing || onlyOne}
                      onClick={() => {
                        setLastAction("unlink");
                        removeDatabase({ database: firstInput, privateKey: lastInput, unlink: true, listOnly: true });
                      }}
                    >
                      {/* Radi SAMO ono dugme koje je pritisnuto. Uslov je ranije
                          bio goli `removing`, pa su oba pisala „Working…" i nije
                          se videlo koja je radnja zapravo krenula. */}
                      {removing && lastAction === "unlink" ? "Working…" : "Remove from my list"}
                    </Button>
                  )}
                  {/* Nudi se samo kad bi baza inače ostala nevidljiva svima:
                      tada „skini i obriši" vodi u čisto stanje umesto u redove
                      koje niko ne gleda. Kad je još neko drži, ovo bi bilo
                      brisanje tuđih podataka. */}
                  {keyValid && holders.length === 0 && (
                    <Button
                      variant="danger"
                      disabled={removing || onlyOne}
                      onClick={() => {
                        setLastAction("purge");
                        removeDatabase({ database: firstInput, privateKey: lastInput, destroy: true, unlink: true });
                      }}
                    >
                      {removing && lastAction === "purge" ? "Working…" : "Delete the database too"}
                    </Button>
                  )}
                  <Button
                    buttonType="text"
                    variant="secondary"
                    disabled={removing}
                    onClick={() => { resetAccessCheck(); setMode("start"); }}
                  >
                    Back
                  </Button>
                  <Button buttonType="text" variant="secondary" disabled={removing} onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </>
            );
          }
          case "account_removed":
            return (
              <>
                <div className={styles.text}>
                  <h2>
                    {lastAction === "purge"
                      ? `Database ${firstInput} is deleted and off your list`
                      : `Database ${firstInput} is off your list`}
                  </h2>
                </div>
                {lastAction === "unlink" && (
                  <p className={styles2?.warning || ""}>
                    It is still on the chain. Add New Database → import brings it
                    back, with the name <strong>{firstInput}</strong> and the
                    same key.
                  </p>
                )}
                <div className={styles.btnGroup}>
                  <Button onClick={handleCancel}>OK</Button>
                </div>
              </>
            );
          default:
            return null;
        }

      case "unlink_database":
        switch (mode) {
          case "start":
            return (
              <>
                <div className={styles.text}>
                  <h2>Delete Database</h2>
                </div>
                <input
                  value={firstInput}
                  onChange={(e) => addFirstInput(e.target.value)}
                  onBlur={() => firstInput && runAccessCheck(firstInput, lastInput)}
                  type="text"
                  placeholder="Enter database name"
                />
                <PrivateKeyField
                  value={lastInput}
                  onChange={(e) => addLastInput(e.target.value)}
                  placeholder="Enter your private key"
                />
                {/* Brojanje referenci, kao kod deljenog pokazivača: uništava tek
                    onaj koji je ostao sam. Dok na ovlašćenju stoji više ključeva,
                    i pražnjenje i brisanje bi odnela podatke koje neko drugi
                    upravo koristi — a taj to ne bi ni saznao dok ne pogleda.

                    Ovo je konvencija GUI-ja, ne invarijanta lanca: ko ima ključ,
                    isto može i mimo nas. Brani od omaške, ne od zle namere. */}
                {sharedWith > 1 && (
                  // NIJE greska nego obavestenje, pa vise nije crveno.
                  // Brana je skinuta 01.09, ali je poruka ostala obojena kao
                  // otkaz — pa je izgledalo kao da je radnja odbijena, iako je
                  // dugme sve vreme radilo. Boja je govorila „stani", tekst
                  // „evo sta ce se desiti".
                  <p className={styles2?.notice || ""}>
                    <b>{sharedWith} keys can write to {firstInput}</b>
                    {(accessResult?.holders || []).length > 0 &&
                      ` — ${accessResult.holders.join(", ")} also list it`}
                    . Both actions below go ahead; this is what each one does to
                    them.
                    {" "}
                    <b>Empty</b> clears every row for them too and they keep
                    access, without being told.
                    {" "}
                    <b>Delete</b> takes the contract off and, since it is your
                    database, takes their keys and list entries with it — they
                    lose access. The account itself survives either way.
                  </p>
                )}
                {/* TRI RADNJE, svaka radi jednu stvar.
                    Ranije je bila jedna sa dva čekiranja, i nijedno nije
                    biralo ono što je korisniku trebalo: struktura je nestajala
                    bez obzira na izbor, jer `drop` nije bio uslovljen. */}
                <p className={styles2?.warning || ""}>
                  Emptying and deleting cannot be undone — the chain keeps no
                  copy to restore from. The account itself always survives; a
                  chain cannot delete one.
                </p>

                <ul className={styles2?.warning || ""}>
                  <li>
                    <b>Empty</b> — clears every row and keeps the table
                    definitions. A table with no rows takes no space on the
                    chain; it appears again with the first write. Use this when
                    an import brought the wrong data but the right structure.
                  </li>
                  <li>
                    <b>Delete</b> — clears the rows, removes the compositions
                    and takes the contract off the account. What is left is a
                    bare account, exactly as it was before its first import.
                  </li>
                </ul>

                {removing && <StepProgress progress={removeProgress} />}
                {removeError && <p className={styles.error}>{removeError}</p>}
                <div className={styles.btnGroup}>
                  {/* Brana `sharedWith > 1` je ovde SKINUTA 31.08.2026, jer
                      nije mogla da se sprovede: brisanje redova
                      (`/api/database/delete`, `/delete-where`) i pražnjenje po
                      kompoziciji (`/api/database/clear`) nemaju nikakvu
                      proveru, a poslednje gađa ISTI endpoint koji „Empty" i
                      koristi. Dakle isto se moglo uraditi jedan ekran dalje,
                      uz klik više. Dugme koje tvrdi da brani nešto što ne
                      brani gore je od dugmeta koje radi — pa sada radi, a
                      poruka iznad kaže koga to pogađa. */}
                  <Button
                    disabled={removing || !firstInput || !lastInput}
                    disabledReason={
                      !firstInput || !lastInput
                        ? "Enter the database name and your private key first."
                        : undefined
                    }
                    onClick={() =>
                      { setLastAction("empty"); removeDatabase({ database: firstInput, privateKey: lastInput, keepStructure: true }); }
                    }
                  >
                    {removing && lastAction === "empty" ? "Working…" : "Empty"}
                  </Button>
                  {/* Brana `sharedWith > 1` je skinuta i sa `Delete`
                      (01.09.2026, Goranova odluka).
                      Razlog: model prava ima DVA nivoa — imas kljuc ili nemas.
                      Razlikovati vlasnika od primaoca znacilo bi uvesti treci
                      nivo koji na lancu ne postoji, a oboje ionako mogu isto
                      (`destroyDatabase` trazi `active`). Do 31.08. je posledica
                      bila da deljenu bazu ne moze obrisati NIKO kroz GUI, ni
                      njen vlasnik.
                      Potvrda kucanjem imena se ne dodaje: `firstInput` JESTE
                      ime baze koje je korisnik vec uneo. */}
                  <Button
                    variant="danger"
                    disabled={removing || !firstInput || !lastInput}
                    disabledReason={
                      !firstInput || !lastInput
                        ? "Enter the database name and your private key first."
                        : undefined
                    }
                    onClick={() =>
                      { setLastAction("delete"); removeDatabase({ database: firstInput, privateKey: lastInput, destroy: true }); }
                    }
                  >
                    {removing && lastAction === "delete" ? "Working…" : "Delete"}
                  </Button>
                  <Button
                    buttonType="text"
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
                {isSubmitting && <ActionLoader />}
              </>
            );
          case "account_removed":
            return (
              <>
                {/* Naslov mora da prati RADNJU. Do 31.08. je tvrdo pisalo
                    „is deleted" i posle pražnjenja — dok je obaveštenje ispod
                    njega u istom trenutku govorilo „Database emptied".
                    Korisnik koji je ispraznio bazu čitao je da je obrisana. */}
                <div className={styles.text}>
                  <h2>
                    {lastAction === "empty"
                      ? `Every row in ${firstInput} is gone. The tables are still there.`
                      : lastAction === "unlink"
                        ? `${firstInput} is off your list. The database itself is untouched.`
                        : lastAction === "purge"
                          ? `${firstInput} is deleted and off your list`
                          : `${firstInput} is deleted. The account itself remains.`}
                  </h2>
                </div>

                {/* Samo tamo gde je kontrakt zaista skinut. Pražnjenje i
                    skidanje sa spiska su povratni, pa im razlika između
                    „zapisano" i „nepovratno" ne menja nijednu odluku. */}
                {/* Ono što je SERVER rekao. Naslov iznad je klijentov i uvek
                    isti; ovde stoji šta se zaista desilo — koliko je redova
                    otišlo, i da li su tuđi ključevi skinuti ili nisu i zašto.
                    Bez ovoga je radnja mogla tiho da odustane od pola posla. */}
                {removedMessage && (
                  <p className={styles2?.notice || ""}>{removedMessage}</p>
                )}

                {(lastAction === "delete" || lastAction === "purge") && (
                  <Irreversible blockNum={destroyedInBlock} />
                )}

                <div className={styles.btnGroup}>
                  <Button onClick={handleCancel}>OK</Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
              </>
            );

          default:
            return null;
        }

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={openModal}
      ariaHideApp={false}
      // onAfterOpen={afterOpenModal}
      // onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Modal"
    >
      <div className={`${styles.privateKey}  ${dark ? styles.dark : ""}`} style={{ position: 'relative' }}>
        {/* Prazan modal je slepa ulica: prozor se iscrta, dugmadi nema, pa ni
            izlaza osim osvežavanja stranice — a radnja je na lancu možda i
            prošla. Tako je 25.08. uspešan uvoz izgledao kao da se GUI zaledio.
            `switch` ima nekoliko `default: return null` grana i lako je dodati
            režim a zaboraviti ekran; zato izlaz stoji OVDE, gde ga nijedan
            budući previd ne može zaobići. */}
        {renderModalContent(typeCall, mode) || (
          <>
            <div className={styles.text}>
              <h2>Nothing to show for this step</h2>
            </div>
            <p className={styles2?.warning || ""}>
              The action may well have gone through — check your database list
              after closing. This screen is missing, which is a fault on our
              side, not something you did.
            </p>
            <div className={styles.btnGroup}>
              <Button onClick={handleCancel}>Close</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AddNewAccount;

AddNewAccount.propTypes = {
  type: PropTypes.string,
  typeCall: PropTypes.string,
};
